import React, { useContext, useEffect, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { toast } from 'react-toastify';
import useOrganizationService from '../../../app/services/hooks/useOrganizationService';
import useHandleErrorFields from '../../../hooks/useHandleErrorFields';
import { AuthContext } from '../../../contexts/AuthContext';
import { mapAuthResponse } from '../../../app/models/Auth';
import InputField from '../../../components/InputField';
import ButtonGeneral from '../../../components/buttons/ButtonGeneral';
import SquareCheckbox from '../../../components/SquareCheckbox/SquareCheckbox';
import PlanCard from '../../Organization/components/PlanCard';
import { LawyerPlan, isSafePaymentUrl } from '../../Organization/types';
import logo_monere from '../../../assets/images/logo_monere.png';
import '../Login/Login.css';

type AccountType = 'independent' | 'firm';

const EMPTY_FORM = {
    first_name: '', last_name: '', email: '', password: '', password_confirm: '',
    organization_name: '', rut: '', phone: '', accept_terms: false, accept_data_processing: false
};

/**
 * Registro self-service de abogados independientes y estudios (R6).
 * Elige tipo de cuenta y plan, crea la cuenta y queda operativo; si el plan
 * es pagado sigue a Flow para registrar la tarjeta.
 */
const RegisterLawyer: React.FC = () => {
    const history = useHistory();
    const location = useLocation();
    const { login } = useContext(AuthContext);
    const { getPublicPlans, registerLawyer, fetchingPlans, fetchingRegister } = useOrganizationService();
    const { fieldErrorMessage, onFocusRemove, setErrors } = useHandleErrorFields();

    const params = new URLSearchParams(location.search);
    const [type, setType] = useState<AccountType | null>(
        params.get('tipo') === 'estudio' ? 'firm' : params.get('tipo') === 'independiente' ? 'independent' : null
    );
    const [plans, setPlans] = useState<LawyerPlan[]>([]);
    const [plan, setPlan] = useState<LawyerPlan | null>(null);
    const [form, setForm] = useState(EMPTY_FORM);
    const [verificationSent, setVerificationSent] = useState<{ email: string; plan: string | null } | null>(null);

    useEffect(() => {
        if (!type) return;
        setPlan(null);
        getPublicPlans(type, {
            onSuccess: (response) => {
                const list: LawyerPlan[] = response.data.plans;
                setPlans(list);
                const wanted = list.find((p) => p.code === params.get('plan'));
                if (wanted) setPlan(wanted);
            },
            onError: (response) => toast.error(response.message)
        });
    }, [type]);

    const set = (field: keyof typeof EMPTY_FORM) => (value: string) => setForm({ ...form, [field]: value });

    const handleSubmit = () => {
        if (form.password !== form.password_confirm) {
            setErrors({ password_confirm: ['Las contraseñas no coinciden.'] });
            return;
        }
        if (!form.accept_terms) {
            setErrors({ accept_terms: ['Debes aceptar los términos y la política de privacidad.'] });
            return;
        }
        if (!form.accept_data_processing) {
            setErrors({ accept_data_processing: ['Debes aceptar el contrato de encargo de tratamiento de datos de tus clientes.'] });
            return;
        }
        registerLawyer({
            first_name: form.first_name,
            last_name: form.last_name,
            email: form.email,
            password: form.password,
            rut: form.rut,
            phone: form.phone,
            organization_type: type,
            organization_name: form.organization_name,
            plan_id: plan?.id ?? '',
            accept_terms: true,
            accept_data_processing: true
        }, {
            onSuccess: (response) => {
                // La cuenta se activa con el enlace que llega al correo
                setVerificationSent({ email: response.data.email, plan: response.data.requested_plan?.name || null });
            },
            onError: (response) => toast.error(response.message),
            onFieldError: (response) => {
                setErrors(response.data);
                toast.error(response.data?.plan_id?.[0] || 'Revisa los datos del formulario');
            }
        });
    };

    const typeCard = (value: AccountType, title: string, description: string, icon: string) => (
        <div className="col-md-6 mb-3">
            <button
                type="button"
                className={`card w-100 h-100 text-start ${type === value ? 'border-primary border-2' : ''}`}
                onClick={() => setType(value)}
                data-testid={`account-type-${value}`}
            >
                <div className="card-body">
                    <i className={`fas ${icon} fa-2x text-primary mb-2`} />
                    <h5 className="mb-1">{title}</h5>
                    <p className="text-muted small mb-0">{description}</p>
                </div>
            </button>
        </div>
    );

    if (verificationSent) {
        return (
            <div className="container py-5" style={{ maxWidth: 620 }}>
                <div className="card shadow-sm">
                    <div className="card-body p-4 p-md-5 text-center" data-testid="verification-sent">
                        <i className="fas fa-envelope-open-text fa-3x text-primary mb-3" />
                        <h3>Revisa tu correo</h3>
                        <p className="text-muted">
                            Te enviamos un enlace a <strong>{verificationSent.email}</strong> para confirmar que es tuyo.
                            Ábrelo para activar tu cuenta.
                        </p>
                        {verificationSent.plan && (
                            <p className="small text-muted">
                                Al activarla te llevaremos a contratar el plan <strong>{verificationSent.plan}</strong>.
                                Mientras tanto quedas con el plan gratuito.
                            </p>
                        )}
                        <a href="/login" className="btn btn-outline-primary btn-sm mt-2">Ir a iniciar sesión</a>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="login-container" style={{ alignItems: 'flex-start', padding: '40px 16px' }}>
            <div className="login-background"><div className="login-background-pattern" /></div>
            <div className="container position-relative" style={{ maxWidth: 1100 }}>
                <div className="card shadow">
                    <div className="card-body p-4 p-md-5">
                        <div className="text-center mb-4">
                            <img src={logo_monere} alt="Monere" style={{ height: 56 }} />
                            <h2 className="mt-3 mb-1">Crea tu cuenta de abogado</h2>
                            <p className="text-muted mb-0">Gestiona las marcas y contratos de tus clientes con tu propia marca.</p>
                        </div>

                        <h5 className="mb-3">1. ¿Cómo trabajas?</h5>
                        <div className="row">
                            {typeCard('independent', 'Abogado independiente', 'Trabajas solo. Una cuenta, un usuario.', 'fa-user-tie')}
                            {typeCard('firm', 'Estudio jurídico', 'Un administrador y su equipo de abogados y asistentes.', 'fa-building')}
                        </div>

                        {type && (
                            <>
                                <h5 className="mt-3 mb-3">2. Elige tu plan</h5>
                                {fetchingPlans && plans.length === 0 ? (
                                    <div className="text-center py-3"><div className="spinner-border text-primary" /></div>
                                ) : (
                                    <div className="row">
                                        {plans.map((item) => (
                                            <div className="col-md-6 col-lg-4 mb-3" key={item.id}>
                                                <PlanCard plan={item} selected={plan?.id === item.id}
                                                          actionLabel={plan?.id === item.id ? 'Elegido' : 'Elegir'}
                                                          onAction={() => setPlan(item)} />
                                            </div>
                                        ))}
                                        {plans.length === 0 && (
                                            <p className="text-muted">
                                                Aún no hay planes publicados para este tipo de cuenta. Puedes crearla igual y elegir tu plan después.
                                            </p>
                                        )}
                                    </div>
                                )}

                                <h5 className="mt-3 mb-3">3. Tus datos</h5>
                                <div className="row">
                                    {type === 'firm' && (
                                        <div className="col-md-6 mb-3">
                                            <InputField label="Nombre del estudio" value={form.organization_name} required
                                                        onChange={set('organization_name')} onFocus={() => onFocusRemove('organization_name')}
                                                        error={fieldErrorMessage('organization_name')} />
                                        </div>
                                    )}
                                    <div className="col-md-6 mb-3">
                                        <InputField label="Nombre" value={form.first_name} required onChange={set('first_name')}
                                                    onFocus={() => onFocusRemove('first_name')} error={fieldErrorMessage('first_name')} />
                                    </div>
                                    <div className="col-md-6 mb-3">
                                        <InputField label="Apellido" value={form.last_name} required onChange={set('last_name')}
                                                    onFocus={() => onFocusRemove('last_name')} error={fieldErrorMessage('last_name')} />
                                    </div>
                                    <div className="col-md-6 mb-3">
                                        <InputField label="Correo" type="email" value={form.email} required onChange={set('email')}
                                                    onFocus={() => onFocusRemove('email')} error={fieldErrorMessage('email')} />
                                    </div>
                                    <div className="col-md-6 mb-3">
                                        <InputField label="RUT" value={form.rut} onChange={set('rut')}
                                                    onFocus={() => onFocusRemove('rut')} error={fieldErrorMessage('rut')} />
                                    </div>
                                    <div className="col-md-6 mb-3">
                                        <InputField label="Teléfono" value={form.phone} onChange={set('phone')}
                                                    onFocus={() => onFocusRemove('phone')} error={fieldErrorMessage('phone')} />
                                    </div>
                                    <div className="col-md-6 mb-3">
                                        <InputField label="Contraseña (mínimo 8 caracteres)" type="password" value={form.password} required
                                                    onChange={set('password')} onFocus={() => onFocusRemove('password')}
                                                    error={fieldErrorMessage('password')} />
                                    </div>
                                    <div className="col-md-6 mb-3">
                                        <InputField label="Repetir contraseña" type="password" value={form.password_confirm} required
                                                    onChange={set('password_confirm')} onFocus={() => onFocusRemove('password_confirm')}
                                                    error={fieldErrorMessage('password_confirm')} />
                                    </div>
                                    <div className="col-12 mb-3">
                                        <SquareCheckbox id="accept_terms" name="accept_terms" value="accept_terms" checked={form.accept_terms}
                                                        onChange={(_v: string, checked: boolean) => { setForm({ ...form, accept_terms: checked }); onFocusRemove('accept_terms'); }}
                                                        label="Acepto los términos del servicio y la política de privacidad. Como abogado, soy responsable de los datos de mis clientes que ingrese a la plataforma." />
                                        <small className="d-block text-muted mt-1">
                                            Lee los <a href="/terminos" target="_blank" rel="noopener noreferrer">términos</a> y
                                            la <a href="/privacidad" target="_blank" rel="noopener noreferrer">política de privacidad</a>.
                                        </small>
                                        {fieldErrorMessage('accept_terms') && <small className="text-danger d-block">{fieldErrorMessage('accept_terms')}</small>}
                                    </div>
                                    <div className="col-12 mb-3">
                                        <SquareCheckbox id="accept_data_processing" name="accept_data_processing" value="accept_data_processing"
                                                        checked={form.accept_data_processing}
                                                        onChange={(_v: string, checked: boolean) => { setForm({ ...form, accept_data_processing: checked }); onFocusRemove('accept_data_processing'); }}
                                                        label="Acepto el contrato de encargo de tratamiento de datos: mi estudio es el responsable de los datos de sus clientes y Monere los trata solo por su cuenta para prestar el servicio." />
                                        <small className="d-block text-muted mt-1">
                                            Lee el <a href="/contrato-encargo" target="_blank" rel="noopener noreferrer">contrato de encargo</a> (Ley 19.628, art. 15 bis).
                                        </small>
                                        {fieldErrorMessage('accept_data_processing') && <small className="text-danger d-block">{fieldErrorMessage('accept_data_processing')}</small>}
                                    </div>
                                </div>

                                <div className="d-flex flex-wrap justify-content-between align-items-center gap-2">
                                    <small className="text-muted">
                                        ¿Ya tienes cuenta? <a href="/login">Inicia sesión</a>
                                    </small>
                                    <ButtonGeneral
                                        onClick={handleSubmit}
                                        title={plan && !plan.is_free ? 'Crear cuenta y pagar' : 'Crear cuenta'}
                                        loading={fetchingRegister}
                                        icon="fa-arrow-right"
                                        iconPosition="right"
                                    />
                                </div>
                            </>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default RegisterLawyer;
